import { useApiClient } from 'bff/ApiClientProvider';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DocumentsDownloadRequest,
  Result,
  ResultOfApplicationFormViewModel,
  ResultStates,
  SigningCancelSigningRequest,
  SigningCreateAndUploadDocumentRequest,
  SigningDeleteConsultantRequest,
  SigningDownloadAndSignRequest,
  SigningFinaliseProposalRequest,
  SigningGetAuditTrailForDocumentRequest,
  SigningGetCapturedSigningDetailsRequest,
  SigningGetSigningProgressSummaryRequest,
  SigningRemoveSignatoryRequest,
  SigningResendDocumentForSigningRequest,
  SigningSaveCapturedSigningDetailsRequest,
  SigningStartSigningRequest,
  SigningSubmitToESignRequest,
  SigningUpsertConsultantRequest,
  SigningUpsertSignatoryRequest
} from 'bff/client';
import { useInvalidateProposalInformation } from '../common/ProposalsInfosApi';
import { UseDisplayErrorMutationOptions } from 'bff/types/display-error-props.type';
import { minutesToMilliseconds, secondsToMilliseconds } from 'date-fns';
import { useProposalIdFromParams } from 'hooks/useProposalIdFromParams';
import { setIsBackgroundSubmitting } from 'hooks/useIsGlobalFetching';
import { useInvalidateGetAllProposals } from '../overview/ProposalOverviewApi';

export const signingSummaryKey = {
  base: () => ['signing-progress-summary'] as const,
  fetch: (request: SigningGetSigningProgressSummaryRequest) => [
    ...signingSummaryKey.base(),
    request
  ],
  fetchDocument: () => [...signingSummaryKey.base(), 'fetch-document'],
  submit: () => [...signingSummaryKey.base(), 'submit'] as const,
  consultant: () => [...signingSummaryKey.base(), 'consultant-upsert'] as const,
  consultantDelete: () => [...signingSummaryKey.base(), 'consultant-delete'] as const
};

export const capturedSigningDetailsKey = {
  base: () => ['captured-signing-details'] as const,
  fetch: (request: SigningGetCapturedSigningDetailsRequest) => [
    ...capturedSigningDetailsKey.base(),
    request
  ],
  submit: () => [...capturedSigningDetailsKey.base(), 'submit'] as const,
  healthCheck: () => [...capturedSigningDetailsKey.base(), 'health-check'],
  downloadDocument: () => [...capturedSigningDetailsKey.base(), 'download-document'],
  uploadDocument: () => [...capturedSigningDetailsKey.base(), 'upload-document'] as const
};

export const useInvalidateCapturedSigningDetails = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: capturedSigningDetailsKey.base(),
      refetchType: 'all'
    });
};

export const getCapturedSigningDetailsOptions = (
  getCapturedSigningDetailsRequest: SigningGetCapturedSigningDetailsRequest
) => {
  const { signingApi } = useApiClient();
  return queryOptions({
    queryKey: capturedSigningDetailsKey.fetch(getCapturedSigningDetailsRequest),
    queryFn: () => signingApi.signingGetCapturedSigningDetails(getCapturedSigningDetailsRequest),
    select: (response) => response.data!,
    gcTime: 0,
    refetchOnWindowFocus: false
  });
};

export const getSigningProgressSummaryOptions = (
  getSigningProgressSummaryRequest: SigningGetSigningProgressSummaryRequest
) => {
  const { signingApi } = useApiClient();
  return queryOptions({
    staleTime: minutesToMilliseconds(10),
    queryKey: signingSummaryKey.fetch(getSigningProgressSummaryRequest),
    queryFn: () => signingApi.signingGetSigningProgressSummary(getSigningProgressSummaryRequest),
    select: (response) => response.data!,
    refetchOnWindowFocus: false
  });
};

export const useResendSigningDocs = (
  options?: UseDisplayErrorMutationOptions<Result, SigningResendDocumentForSigningRequest>
) => {
  const { signingApi } = useApiClient();
  return useMutation({
    mutationFn: (model: SigningResendDocumentForSigningRequest) =>
      signingApi.signingResendDocumentForSigning(model),
    onSuccess: async (data, variables, ctx) => {
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

export const getAuditTrailForDocumentOptions = (
  queryParams: SigningGetAuditTrailForDocumentRequest
) => {
  const { signingApi } = useApiClient();
  return queryOptions({
    staleTime: secondsToMilliseconds(10),
    queryKey: ['getAuditTrailForDocument'],
    queryFn: () => signingApi.signingGetAuditTrailForDocument(queryParams),
    select: (response) => response
  });
};

export const useStartSigning = (
  options?: UseDisplayErrorMutationOptions<Result, SigningStartSigningRequest>
) => {
  const { signingApi } = useApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  return useMutation({
    mutationFn: (clientDetails: SigningStartSigningRequest) =>
      signingApi.signingStartSigning(clientDetails),
    onSuccess: async (data, variables, ctx) => {
      await invalidateProposalInformation();
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

export const useDownloadAndSign = (
  options?: UseDisplayErrorMutationOptions<
    ResultOfApplicationFormViewModel,
    SigningDownloadAndSignRequest
  >
) => {
  const { signingApi } = useApiClient();
  const proposalId = useProposalIdFromParams();
  const queryClient = useQueryClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateProposalOverview = useInvalidateGetAllProposals();
  return useMutation({
    mutationFn: (downloadRequest: SigningDownloadAndSignRequest) => {
      setIsBackgroundSubmitting({
        proposalId: proposalId,
        isBackgroundSubmitting: true
      });
      return signingApi.signingDownloadAndSign(downloadRequest);
    },
    onSuccess: async (data, variables, ctx) => {
      await Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: signingSummaryKey.base(),
          refetchType: 'all'
        }),
        invalidateProposalInformation(),
        invalidateProposalOverview()
      ]);
      setIsBackgroundSubmitting({
        proposalId: undefined,
        isBackgroundSubmitting: false
      });
      options?.onSuccess?.(data, variables, ctx);
    },
    onError: () => {
      setIsBackgroundSubmitting({
        proposalId: undefined,
        isBackgroundSubmitting: false
      });
    }
  });
};

export const useESign = (
  options?: UseDisplayErrorMutationOptions<Result, SigningSubmitToESignRequest>
) => {
  const { signingApi } = useApiClient();
  const proposalId = useProposalIdFromParams();
  const queryClient = useQueryClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateProposalOverview = useInvalidateGetAllProposals();
  return useMutation({
    mutationFn: (eSignRequest: SigningSubmitToESignRequest) => {
      setIsBackgroundSubmitting({
        proposalId: proposalId,
        isBackgroundSubmitting: true
      });
      return signingApi.signingSubmitToESign(eSignRequest);
    },
    onSuccess: async (data, variables, ctx) => {
      await Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: signingSummaryKey.base(),
          refetchType: 'all'
        }),
        invalidateProposalInformation(),
        invalidateProposalOverview()
      ]);
      setIsBackgroundSubmitting({
        proposalId: undefined,
        isBackgroundSubmitting: false
      });
      options?.onSuccess?.(data, variables, ctx);
    },
    onError: () => {
      setIsBackgroundSubmitting({
        proposalId: undefined,
        isBackgroundSubmitting: false
      });
    }
  });
};

export const useInOfficeESign = (
  options?: UseDisplayErrorMutationOptions<Result, SigningSubmitToESignRequest>
) => {
  const { signingApi } = useApiClient();
  const proposalId = useProposalIdFromParams();
  const queryClient = useQueryClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateProposalOverview = useInvalidateGetAllProposals();
  return useMutation({
    mutationFn: (eSignRequest: SigningSubmitToESignRequest) => {
      setIsBackgroundSubmitting({
        proposalId: proposalId,
        isBackgroundSubmitting: true
      });
      return signingApi.signingSubmitForInOfficeESign(eSignRequest);
    },
    onSuccess: async (data, variables, ctx) => {
      await Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: signingSummaryKey.base(),
          refetchType: 'all'
        }),
        invalidateProposalInformation(),
        invalidateProposalOverview()
      ]);
      setIsBackgroundSubmitting({
        proposalId: undefined,
        isBackgroundSubmitting: false
      });
      options?.onSuccess?.(data, variables, ctx);
    },
    onError: () => {
      setIsBackgroundSubmitting({
        proposalId: undefined,
        isBackgroundSubmitting: false
      });
    }
  });
};

export const useSaveCapturedSigningDetails = (
  options?: UseDisplayErrorMutationOptions<Result, SigningSaveCapturedSigningDetailsRequest>
) => {
  const { signingApi } = useApiClient();
  const queryClient = useQueryClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const proposalId = useProposalIdFromParams();

  return useMutation({
    mutationKey: capturedSigningDetailsKey.submit(),
    mutationFn: (request: SigningSaveCapturedSigningDetailsRequest) => {
      setIsBackgroundSubmitting({
        proposalId: proposalId,
        isBackgroundSubmitting: true
      });
      return signingApi.signingSaveCapturedSigningDetails(request);
    },
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        options?.onSuccess?.(data, variables, ctx);

        await Promise.allSettled([
          queryClient.invalidateQueries({
            queryKey: capturedSigningDetailsKey.base(),
            refetchType: 'all'
          }),
          invalidateProposalInformation()
        ]);
      }
      setIsBackgroundSubmitting({
        proposalId: undefined,
        isBackgroundSubmitting: false
      });
    },
    onError: () => {
      setIsBackgroundSubmitting({
        proposalId: undefined,
        isBackgroundSubmitting: false
      });
    }
  });
};

export const useUploadDocument = (
  options?: UseDisplayErrorMutationOptions<Result, SigningCreateAndUploadDocumentRequest>
) => {
  const { signingApi } = useApiClient();
  const queryClient = useQueryClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();

  return useMutation({
    mutationKey: capturedSigningDetailsKey.uploadDocument(),
    mutationFn: (request: SigningCreateAndUploadDocumentRequest) =>
      signingApi.signingCreateAndUploadDocument(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        options?.onSuccess?.(data, variables, ctx);

        await Promise.allSettled([
          queryClient.invalidateQueries({
            queryKey: capturedSigningDetailsKey.base(),
            refetchType: 'all'
          }),
          queryClient.invalidateQueries({
            queryKey: signingSummaryKey.base(),
            refetchType: 'all'
          }),
          invalidateProposalInformation()
        ]);
      }
    },
    onError: (error, request, otherParams) => options?.onError?.(error, request, otherParams)
  });
};

export const getQuicklySignHealthCheckOptions = () => {
  const { signingApi } = useApiClient();
  return queryOptions({
    staleTime: minutesToMilliseconds(3),
    queryKey: capturedSigningDetailsKey.healthCheck(),
    queryFn: () => signingApi.signingQuicklySignHealthCheck(),
    refetchOnWindowFocus: false
  });
};

export const useFinaliseProposal = (
  options?: UseDisplayErrorMutationOptions<Result, SigningFinaliseProposalRequest>
) => {
  const { signingApi } = useApiClient();
  const queryClient = useQueryClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();

  return useMutation({
    mutationKey: signingSummaryKey.submit(),
    mutationFn: (request: SigningFinaliseProposalRequest) =>
      signingApi.signingFinaliseProposal(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        options?.onSuccess?.(data, variables, ctx);

        await Promise.allSettled([
          queryClient.invalidateQueries({
            queryKey: signingSummaryKey.base(),
            refetchType: 'all'
          }),
          invalidateProposalInformation()
        ]);
      }
    }
  });
};

export const useCancelSigning = (
  options?: UseDisplayErrorMutationOptions<Result, SigningCancelSigningRequest>
) => {
  const { signingApi } = useApiClient();
  const queryClient = useQueryClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();

  return useMutation({
    mutationKey: signingSummaryKey.submit(),
    mutationFn: (request: SigningCancelSigningRequest) => signingApi.signingCancelSigning(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        options?.onSuccess?.(data, variables, ctx);

        await Promise.allSettled([
          queryClient.invalidateQueries({
            queryKey: signingSummaryKey.base(),
            refetchType: 'all'
          }),
          queryClient.invalidateQueries({
            queryKey: capturedSigningDetailsKey.base(),
            refetchType: 'all'
          }),
          invalidateProposalInformation()
        ]);
      }
    }
  });
};

export const useUpsertConsultant = (
  options?: UseDisplayErrorMutationOptions<Result, SigningUpsertConsultantRequest>
) => {
  const { signingApi } = useApiClient();

  return useMutation({
    mutationKey: signingSummaryKey.consultant(),
    mutationFn: (request: SigningUpsertConsultantRequest) =>
      signingApi.signingUpsertConsultant(request),
    onSuccess: async (data, variables, ctx) => {
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

export const useUpsertSignatory = (
  options?: UseDisplayErrorMutationOptions<Result, SigningUpsertSignatoryRequest>
) => {
  const { signingApi } = useApiClient();
  const queryClient = useQueryClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();

  return useMutation({
    mutationKey: signingSummaryKey.consultant(),
    mutationFn: (request: SigningUpsertSignatoryRequest) =>
      signingApi.signingUpsertSignatory(request),
    onSuccess: async (data, variables, ctx) => {
      options?.onSuccess?.(data, variables, ctx);
      await Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: signingSummaryKey.base(),
          refetchType: 'all'
        }),
        queryClient.invalidateQueries({
          queryKey: capturedSigningDetailsKey.base(),
          refetchType: 'all'
        }),
        invalidateProposalInformation()
      ]);
    }
  });
};

export const useDeleteConsultant = (
  options?: UseDisplayErrorMutationOptions<Result, SigningDeleteConsultantRequest>
) => {
  const { signingApi } = useApiClient();

  return useMutation({
    mutationKey: signingSummaryKey.consultantDelete(),
    mutationFn: (request: SigningDeleteConsultantRequest) =>
      signingApi.signingDeleteConsultant(request),
    onSuccess: async (data, variables, ctx) => {
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

export const useDeleteSignatory = (
  options?: UseDisplayErrorMutationOptions<Result, SigningRemoveSignatoryRequest>
) => {
  const { signingApi } = useApiClient();

  return useMutation({
    mutationKey: signingSummaryKey.consultantDelete(),
    mutationFn: (request: SigningRemoveSignatoryRequest) =>
      signingApi.signingRemoveSignatory(request),
    onSuccess: async (data, variables, ctx) => {
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

export const useDownloadQuicklySignDocument = (
  options?: UseDisplayErrorMutationOptions<Result, DocumentsDownloadRequest>
) => {
  const { documentsApi } = useApiClient();

  return useMutation({
    mutationKey: signingSummaryKey.fetchDocument(),
    mutationFn: (request: DocumentsDownloadRequest) => documentsApi.documentsDownload(request),
    onSuccess: async (data, variables, ctx) => {
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};
